// src/hooks/useEntitlements.js
//
// Hook de ENTITLEMENTS para UX (NÃO é autoridade de quota — isso é server).
// Pluga no provider realtime principal (UserContext: onSnapshot de USER/PACIENTE).
//
// O vínculo com academia vem do espelho USER.PARCEIRO (escrito só pelo
// servidor: tier do parceiro, plano da academia e benefícios que ela deu). O
// antigo listener de PARTNER_MEMBERSHIP saiu: as regras nunca deixaram o app
// ler aquele documento, então ele só produzia erro e "sem vínculo".
//
// Retorna: { tier, tierUser, tierParceiro, parceiro, beneficios,
//            isPremiumEffective, isVinculado, quotas, can, podeUsar, restante, loading }.

import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useUser } from "../contexts/UserContext";
import {
  TIERS,
  QUOTAS,
  TICKET_FIELD,
  TICKET_SALDO_TIPO,
  FEATURES,
} from "../config/planoConfig";
import {
  resolverPerfilDeAcesso,
  restanteParaFeature,
  quotaFor,
  featureLiberada,
  tierPago,
} from "../utils/entitlements";

export function useEntitlements() {
  const { currentUser, user, mealScans, bodyScans } = useUser();

  // Tickets COMPRADOS do scanfood (Pacote de Tickets Extra): saldo vivo de
  // USER/{uid}/TICKETS_SALDO/REFEICAO (rules permitem o dono ler). Fail-safe:
  // erro de leitura vale como 0 — o server continua autoritativo no gate.
  const [compradoScanfood, setCompradoScanfood] = useState(0);

  useEffect(() => {
    const uid = currentUser?.uid;
    const tipo = TICKET_SALDO_TIPO.scanfood;
    if (!uid || !tipo) {
      setCompradoScanfood(0);
      return undefined;
    }
    const ref = doc(db, "USER", uid, "TICKETS_SALDO", tipo);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const n = Number(snap.exists() ? snap.data()?.COMPRADO_SALDO : 0);
        setCompradoScanfood(Number.isFinite(n) && n > 0 ? n : 0);
      },
      () => setCompradoScanfood(0)
    );
    return () => unsub();
  }, [currentUser?.uid]);

  const subscriptionInfo = user?.SUBSCRIPTION_INFO || {};
  const criador = user?.CRIADOR || null;
  const parceiro = user?.PARCEIRO || null;

  const perfil = useMemo(
    () => resolverPerfilDeAcesso({ subscriptionInfo, criador, parceiro }),
    [subscriptionInfo, criador, parceiro]
  );
  const { tier, tierUser, tierParceiro, beneficios } = perfil;

  // "Premium efetivo" = o pacote pago (Premium, academia Premium, Criador).
  // Vinculado a academia Essencial NÃO é: tem os limites do grátis.
  const isPremiumEffective = tierPago(tier);
  const isVinculado = !!tierParceiro;
  // Criador Parceiro com benefícios (selo, card da plataforma, 10 scans).
  const isCriador = tier === TIERS.CRIADOR;

  // QUOTAS do tier corrente (com o limite da academia, se ela deu um).
  const quotas = useMemo(() => {
    const out = {};
    for (const feature of Object.keys(QUOTAS)) {
      out[feature] = quotaFor(feature, tier, beneficios);
    }
    return out;
  }, [tier, beneficios]);

  const logsFor = (feature) => {
    if (feature === "scanfood") return mealScans || [];
    if (feature === "scanbody") return bodyScans || [];
    // geradorTreino/geradorDieta: as subcoleções CURRENT/TREINO e DIETA não são
    // carregadas no UserContext. Sem janela client-side → server é a autoridade.
    return [];
  };

  const ticketDisponivelFor = (feature) => {
    // Saldo COMPRADO em TICKETS_SALDO (hoje só scanfood).
    if (TICKET_SALDO_TIPO[feature]) {
      return feature === "scanfood" ? compradoScanfood : 0;
    }
    const field = TICKET_FIELD[feature];
    if (!field) return 0;
    return Number(subscriptionInfo?.[field] ?? user?.[field] ?? 0) || 0;
  };

  /**
   * Restante exibível por quota (UX). Para o gerador de treino/dieta, retorna
   * supportsClient:false (sem janela carregada — consultar server).
   */
  const restante = (feature) => {
    const info = restanteParaFeature({
      feature,
      tier,
      beneficios,
      logs: logsFor(feature),
      ticketDisponivel: ticketDisponivelFor(feature),
      nowMs: Date.now(),
    });
    const gerador = feature === "geradorTreino" || feature === "geradorDieta";
    const supportsClient = !gerador || info.ilimitado;
    return { ...info, supportsClient };
  };

  /** Flag de feature visual (mapaMuscularInteligente, graficosEvolucao, analiseInteligente, ads). */
  const can = (featureFlag) => {
    if (!FEATURES[featureFlag]) return false;
    return featureLiberada(featureFlag, tier, beneficios);
  };

  /** Pode usar uma feature COM QUOTA agora? (restante > 0 ou ilimitada). */
  const podeUsar = (feature) => {
    if (!QUOTAS[feature]) return can(feature); // não-quota → trata como flag
    const info = restante(feature);
    if (info.ilimitado) return true;
    if (!info.supportsClient) return true; // deixa o server decidir (não bloqueia UX)
    return info.restante > 0;
  };

  const loading = !user;

  return {
    tier,
    tierUser,
    tierParceiro,
    parceiro,
    beneficios,
    isPremiumEffective,
    isVinculado,
    isCriador,
    criador,
    quotas,
    can,
    podeUsar,
    restante,
    loading,
  };
}

export default useEntitlements;
